import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BarChart3, TrendingUp, TrendingDown, Users, Activity, Clock,
  DollarSign, Award, AlertTriangle, CheckCircle2, ArrowUpRight,
  ArrowDownRight, Target, Calendar, Building2, HeartPulse, FileText
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell
} from 'recharts';

const COLORS = ['#0d9488', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export function ExecutiveDashboard() {
  const [timeRange, setTimeRange] = useState('mtd');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    // Simulate loading dashboard data
    setTimeout(() => {
      setDashboardData({
        summary: {
          current_census: 127,
          census_change: 3,
          admissions_mtd: 45,
          admissions_change: 12,
          discharges_mtd: 38,
          average_los: 72.5,
          bed_utilization: 87.3,
        },
        quality: {
          cahps_overall: 94.2,
          cahps_change: 2.3,
          readmission_rate: 2.3,
          falls_per_1000: 0.8,
          pain_managed: 92.5,
        },
        financial: {
          revenue_mtd: 847500,
          revenue_target: 900000,
          revenue_variance: -5.8,
          avg_revenue_per_day: 425,
          days_in_ar: 28,
          denial_rate: 3.2,
        },
        staff: {
          total_staff: 156,
          fte: 142.5,
          open_positions: 8,
          turnover_ytd: 12.5,
          overtime_hours: 342,
        },
        trends: {
          census: Array.from({ length: 30 }, (_, i) => ({
            date: `Day ${i + 1}`,
            value: 120 + Math.floor(Math.random() * 15),
          })),
          admissions: Array.from({ length: 12 }, (_, i) => ({
            month: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i],
            admissions: 35 + Math.floor(Math.random() * 20),
            discharges: 30 + Math.floor(Math.random() * 20),
          })),
          quality: Array.from({ length: 4 }, (_, i) => ({
            quarter: `Q${i + 1}`,
            score: 88 + Math.floor(Math.random() * 10),
          })),
          revenue: Array.from({ length: 12 }, (_, i) => ({
            month: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i],
            actual: 750000 + Math.floor(Math.random() * 200000),
            target: 850000,
          })),
        },
        alerts: [
          { type: 'warning', title: 'Staffing Alert', message: 'RN coverage below target for next week', priority: 'high' },
          { type: 'info', title: 'Quality Achievement', message: 'CAHPS scores improved 2.3% this quarter', priority: 'low' },
          { type: 'critical', title: 'Compliance Deadline', message: '5 recertifications due within 7 days', priority: 'critical' },
        ],
        referralSources: [
          { name: 'Hospital A', value: 35 },
          { name: 'Hospital B', value: 25 },
          { name: 'Physician Offices', value: 20 },
          { name: 'SNF/ALF', value: 12 },
          { name: 'Other', value: 8 },
        ],
      });
      setLoading(false);
    }, 500);
  }, [timeRange]);

  if (loading || !dashboardData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const { summary, quality, financial, staff, trends, alerts, referralSources } = dashboardData;

  return (
    <div className="p-6 space-y-6 bg-slate-50 dark:bg-slate-900 min-h-screen" data-testid="executive-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Executive Dashboard</h1>
          <p className="text-muted-foreground">Real-time organizational performance metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={timeRange === 'mtd' ? 'default' : 'outline'} onClick={() => setTimeRange('mtd')}>
            MTD
          </Button>
          <Button variant={timeRange === 'qtd' ? 'default' : 'outline'} onClick={() => setTimeRange('qtd')}>
            QTD
          </Button>
          <Button variant={timeRange === 'ytd' ? 'default' : 'outline'} onClick={() => setTimeRange('ytd')}>
            YTD
          </Button>
        </div>
      </div>

      {/* KPI Cards Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-100 text-sm">Current Census</p>
                <p className="text-3xl font-bold">{summary.current_census}</p>
                <div className="flex items-center gap-1 mt-1">
                  {summary.census_change > 0 ? (
                    <ArrowUpRight className="w-4 h-4 text-teal-200" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 text-teal-200" />
                  )}
                  <span className="text-sm text-teal-200">{Math.abs(summary.census_change)} from last month</span>
                </div>
              </div>
              <Users className="w-10 h-10 text-teal-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Admissions MTD</p>
                <p className="text-3xl font-bold">{summary.admissions_mtd}</p>
                <div className="flex items-center gap-1 mt-1">
                  <ArrowUpRight className="w-4 h-4 text-blue-200" />
                  <span className="text-sm text-blue-200">+{summary.admissions_change}% vs last month</span>
                </div>
              </div>
              <Activity className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Avg Length of Stay</p>
                <p className="text-3xl font-bold">{summary.average_los}</p>
                <p className="text-sm text-amber-200 mt-1">days</p>
              </div>
              <Clock className="w-10 h-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Bed Utilization</p>
                <p className="text-3xl font-bold">{summary.bed_utilization}%</p>
                <Progress value={summary.bed_utilization} className="mt-2 bg-purple-400" />
              </div>
              <Building2 className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Census Trend Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Census Trend (30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trends.census}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Area type="monotone" dataKey="value" stroke="#0d9488" fill="#0d9488" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Alerts Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Active Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              <div className="space-y-3">
                {alerts.map((alert, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg border ${
                      alert.priority === 'critical' ? 'bg-red-50 border-red-200 dark:bg-red-900/20' :
                      alert.priority === 'high' ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20' :
                      'bg-blue-50 border-blue-200 dark:bg-blue-900/20'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {alert.type === 'critical' && <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />}
                      {alert.type === 'warning' && <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />}
                      {alert.type === 'info' && <CheckCircle2 className="w-4 h-4 text-blue-500 mt-0.5" />}
                      <div>
                        <p className="font-medium text-sm">{alert.title}</p>
                        <p className="text-xs text-muted-foreground">{alert.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Different Views */}
      <Tabs defaultValue="quality" className="space-y-4">
        <TabsList>
          <TabsTrigger value="quality">Quality Metrics</TabsTrigger>
          <TabsTrigger value="financial">Financial Performance</TabsTrigger>
          <TabsTrigger value="staffing">Staffing</TabsTrigger>
          <TabsTrigger value="referrals">Referral Sources</TabsTrigger>
        </TabsList>

        {/* Quality Tab */}
        <TabsContent value="quality" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">CAHPS Overall</p>
                    <p className="text-2xl font-bold">{quality.cahps_overall}%</p>
                  </div>
                  <Badge className="bg-green-100 text-green-800">+{quality.cahps_change}%</Badge>
                </div>
                <Progress value={quality.cahps_overall} className="mt-2" />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pain Managed</p>
                    <p className="text-2xl font-bold">{quality.pain_managed}%</p>
                  </div>
                  <HeartPulse className="w-8 h-8 text-pink-500" />
                </div>
                <Progress value={quality.pain_managed} className="mt-2" />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Readmission Rate</p>
                    <p className="text-2xl font-bold">{quality.readmission_rate}%</p>
                  </div>
                  <Badge className="bg-green-100 text-green-800">Low</Badge>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Falls/1000 Days</p>
                    <p className="text-2xl font-bold">{quality.falls_per_1000}</p>
                  </div>
                  <Badge className="bg-green-100 text-green-800">Below Target</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Revenue vs Target</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trends.revenue}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="month" fontSize={12} />
                      <YAxis fontSize={12} tickFormatter={(v) => `$${v/1000}k`} />
                      <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                      <Legend />
                      <Bar dataKey="actual" fill="#0d9488" name="Actual" />
                      <Bar dataKey="target" fill="#94a3b8" name="Target" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <div className="space-y-4">
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Revenue MTD</p>
                  <p className="text-2xl font-bold">${(financial.revenue_mtd / 1000).toFixed(0)}K</p>
                  <div className="flex items-center gap-1 mt-1">
                    <span className={`text-sm ${financial.revenue_variance < 0 ? 'text-red-500' : 'text-green-500'}`}>
                      {financial.revenue_variance}% vs target
                    </span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Days in AR</p>
                  <p className="text-2xl font-bold">{financial.days_in_ar}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Denial Rate</p>
                  <p className="text-2xl font-bold">{financial.denial_rate}%</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Staffing Tab */}
        <TabsContent value="staffing" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <Users className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                <p className="text-2xl font-bold">{staff.total_staff}</p>
                <p className="text-sm text-muted-foreground">Total Staff</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Target className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p className="text-2xl font-bold">{staff.fte}</p>
                <p className="text-sm text-muted-foreground">FTE Count</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                <p className="text-2xl font-bold">{staff.open_positions}</p>
                <p className="text-sm text-muted-foreground">Open Positions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <TrendingDown className="w-8 h-8 mx-auto mb-2 text-red-500" />
                <p className="text-2xl font-bold">{staff.turnover_ytd}%</p>
                <p className="text-sm text-muted-foreground">Turnover YTD</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                <p className="text-2xl font-bold">{staff.overtime_hours}</p>
                <p className="text-sm text-muted-foreground">OT Hours MTD</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Referrals Tab */}
        <TabsContent value="referrals" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Referral Sources Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={referralSources}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {referralSources.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Top Referral Sources</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {referralSources.map((source, idx) => (
                    <div key={idx} className="flex items-center gap-4">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="font-medium">{source.name}</span>
                          <span className="text-muted-foreground">{source.value}%</span>
                        </div>
                        <Progress value={source.value} className="h-2" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default ExecutiveDashboard;
